import { concatBytes, copyBytes, utf8 } from "./bytes.js";
import type { OwnedBytes } from "./bytes.js";
import { BlindBindError } from "./errors.js";

export const PROTOCOL_VERSION = 1;
export const MAX_PLAINTEXT_FRAME_BYTES = 64 << 10;

const magic = utf8("BLB1");
const nonceBytes = 32;
const encryptedHeaderBytes = 13;
const authenticationTagBytes = 16;
const protocolInfo = utf8("BLINDBIND-V1");

export const enum FrameType {
  ServerHello = 1,
  ClientHello = 2,
  Finish = 3,
  Data = 4,
}

export interface HelloFrame {
  type: FrameType.ServerHello | FrameType.ClientHello;
  nonce: OwnedBytes;
  publicKey: OwnedBytes;
}

export interface SessionKeys {
  clientToHost: CryptoKey;
  hostToClient: CryptoKey;
}

export interface EncryptedFrame {
  type: FrameType.Finish | FrameType.Data;
  sequence: bigint;
  header: OwnedBytes;
  ciphertext: OwnedBytes;
}

export function encodeHello(
  type: FrameType.ServerHello | FrameType.ClientHello,
  nonce: Uint8Array<ArrayBufferLike>,
  publicKey: Uint8Array<ArrayBufferLike>,
): OwnedBytes {
  if (nonce.byteLength !== nonceBytes) throw new Error("Handshake nonce must contain exactly 32 bytes");
  if (publicKey.byteLength < 1 || publicKey.byteLength > 0xffff)
    throw new Error("Handshake public key length is invalid");
  const result = new Uint8Array(4 + 1 + nonceBytes + 2 + publicKey.byteLength);
  result.set(magic, 0);
  result[4] = type;
  result.set(nonce, 5);
  new DataView(result.buffer).setUint16(5 + nonceBytes, publicKey.byteLength, false);
  result.set(publicKey, 5 + nonceBytes + 2);
  return result;
}

export function decodeHello(
  value: Uint8Array<ArrayBufferLike>,
  expectedType: FrameType.ServerHello | FrameType.ClientHello,
): HelloFrame {
  const minimum = 4 + 1 + nonceBytes + 2 + 1;
  if (value.byteLength < minimum || !hasMagic(value) || value[4] !== expectedType) {
    throw new BlindBindError("protocol_error", "Handshake hello frame is invalid");
  }
  const publicKeyLength = new DataView(value.buffer, value.byteOffset, value.byteLength).getUint16(
    5 + nonceBytes,
    false,
  );
  if (value.byteLength !== 5 + nonceBytes + 2 + publicKeyLength) {
    throw new BlindBindError("protocol_error", "Handshake public key length is invalid");
  }
  return {
    type: expectedType,
    nonce: copyBytes(value.subarray(5, 5 + nonceBytes)),
    publicKey: copyBytes(value.subarray(5 + nonceBytes + 2)),
  };
}

export function encodeEncryptedFrame(
  type: FrameType.Finish | FrameType.Data,
  sequence: bigint,
  ciphertext: Uint8Array<ArrayBufferLike>,
): OwnedBytes {
  if (sequence < 0n || sequence > 0xffffffffffffffffn) throw new Error("Frame sequence is outside uint64 range");
  if (ciphertext.byteLength < authenticationTagBytes)
    throw new Error("Encrypted frame is missing its authentication tag");
  const header = encryptedHeader(type, sequence);
  return concatBytes(header, ciphertext);
}

export function decodeEncryptedFrame(
  value: Uint8Array<ArrayBufferLike>,
  expectedType: FrameType.Finish | FrameType.Data,
): EncryptedFrame {
  if (
    value.byteLength < encryptedHeaderBytes + authenticationTagBytes ||
    !hasMagic(value) ||
    value[4] !== expectedType
  ) {
    throw new BlindBindError("protocol_error", "Encrypted frame is invalid");
  }
  const header = copyBytes(value.subarray(0, encryptedHeaderBytes));
  const sequence = new DataView(value.buffer, value.byteOffset, value.byteLength).getBigUint64(5, false);
  return { type: expectedType, sequence, header, ciphertext: copyBytes(value.subarray(encryptedHeaderBytes)) };
}

export async function deriveSessionKeys(
  sharedSecret: ArrayBuffer | ArrayBufferView<ArrayBufferLike>,
  transcriptHash: OwnedBytes,
): Promise<SessionKeys> {
  const master = await crypto.subtle.importKey("raw", copyBytes(sharedSecret), "HKDF", false, ["deriveBits"]);
  const material = new Uint8Array(
    await crypto.subtle.deriveBits(
      { name: "HKDF", hash: "SHA-256", salt: transcriptHash, info: protocolInfo },
      master,
      512,
    ),
  );
  const clientToHost = await crypto.subtle.importKey("raw", material.slice(0, 32), { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
  const hostToClient = await crypto.subtle.importKey("raw", material.slice(32, 64), { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
  material.fill(0);
  return { clientToHost, hostToClient };
}

export async function encryptFramePayload(
  key: CryptoKey,
  type: FrameType.Finish | FrameType.Data,
  sequence: bigint,
  plaintext: Uint8Array<ArrayBufferLike>,
  extraAssociatedData?: Uint8Array<ArrayBufferLike>,
): Promise<OwnedBytes> {
  const header = encryptedHeader(type, sequence);
  const associatedData = extraAssociatedData ? concatBytes(header, extraAssociatedData) : header;
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonceForSequence(sequence), additionalData: associatedData, tagLength: 128 },
      key,
      copyBytes(plaintext),
    ),
  );
  return encodeEncryptedFrame(type, sequence, ciphertext);
}

export async function decryptFramePayload(
  key: CryptoKey,
  frame: EncryptedFrame,
  extraAssociatedData?: Uint8Array<ArrayBufferLike>,
): Promise<OwnedBytes> {
  const associatedData = extraAssociatedData ? concatBytes(frame.header, extraAssociatedData) : frame.header;
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: nonceForSequence(frame.sequence), additionalData: associatedData, tagLength: 128 },
        key,
        frame.ciphertext,
      ),
    );
  } catch (error) {
    throw new BlindBindError("protocol_error", "Encrypted frame authentication failed", { cause: error });
  }
}

export async function transcriptHash(
  serverHello: Uint8Array<ArrayBufferLike>,
  clientHello: Uint8Array<ArrayBufferLike>,
): Promise<OwnedBytes> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", concatBytes(serverHello, clientHello)));
}

export function sessionIDFromTranscript(hash: Uint8Array<ArrayBufferLike>): OwnedBytes {
  return copyBytes(hash.subarray(0, 16));
}

function encryptedHeader(type: FrameType.Finish | FrameType.Data, sequence: bigint): OwnedBytes {
  const header = new Uint8Array(encryptedHeaderBytes);
  header.set(magic, 0);
  header[4] = type;
  new DataView(header.buffer).setBigUint64(5, sequence, false);
  return header;
}

function nonceForSequence(sequence: bigint): OwnedBytes {
  const nonce = new Uint8Array(12);
  new DataView(nonce.buffer).setBigUint64(4, sequence, false);
  return nonce;
}

function hasMagic(value: Uint8Array<ArrayBufferLike>): boolean {
  return value[0] === magic[0] && value[1] === magic[1] && value[2] === magic[2] && value[3] === magic[3];
}
